import { ArrowLeft, CheckCircle2, MapPin, MessageSquareText, ShieldCheck, Star } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { StatusNotice } from '../components/StatusNotice';
import { api, formatDistance, formatMoney } from '../lib/api';

export function RequestDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<{ request: Record<string, any>; offers: Array<Record<string, any>> } | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyOfferId, setBusyOfferId] = useState('');

  async function load() {
    try { setData(await api.get(`/api/requests/${id}`)); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Could not load request.'); }
  }
  useEffect(() => { void load(); }, [id]);

  async function shortlist(offerId: string) {
    setBusyOfferId(offerId);
    try { await api.post(`/api/requests/${id}/offers/${offerId}/shortlist`); setNotice('Offer shortlisted.'); await load(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Could not shortlist offer.'); }
    finally { setBusyOfferId(''); }
  }

  async function accept(offerId: string) {
    setBusyOfferId(offerId); setError('');
    try {
      const result = await api.post<{ conversationId: string }>(`/api/requests/${id}/offers/${offerId}/accept`);
      navigate(`/messages/${result.conversationId}`);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Could not choose this offer.'); }
    finally { setBusyOfferId(''); }
  }

  async function message(offer: Record<string, any>) {
    setBusyOfferId(offer.id);
    try {
      const result = await api.post<{ id: string }>('/api/conversations', {
        agencyId: offer.agency_id,
        hostelId: offer.hostel_id,
        agencyOfferId: offer.agency_offer_id || undefined,
        openRequestId: id,
        subject: 'Follow-up on open request',
        message: `Hello, I would like to discuss your offer for ${offer.hostel_name}. Please confirm the latest availability and viewing options.`,
      });
      navigate(`/messages/${result.id}`);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Could not start conversation.'); }
    finally { setBusyOfferId(''); }
  }

  async function cancel() {
    try { await api.post(`/api/requests/${id}/cancel`); setNotice('Request cancelled.'); await load(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Could not cancel request.'); }
  }

  if (!data) return <div className="page">{error ? <StatusNotice type="error">{error}</StatusNotice> : <div className="loading-state"><span className="spinner"/>Loading request…</div>}</div>;
  const request = data.request;
  const requestOpen = request.status === 'open' || request.status === 'matched';

  return <div className="page">
    <div className="breadcrumbs"><Link to="/requests"><ArrowLeft size={14}/> Requests</Link><span>/</span><span>{id}</span></div>
    <PageHeader eyebrow={request.status} title={`${request.room_type || 'Hostel'} near ${request.campus_name}`} description={`Budget up to ${formatMoney(request.budget_max)} · ${request.occupancy || 'Flexible'} occupant(s) · move-in ${request.move_in_date || 'flexible'}`} actions={requestOpen ? <button className="ghost-button danger" onClick={() => void cancel()}>Cancel request</button> : undefined}/>
    {error && <StatusNotice type="error">{error}</StatusNotice>}
    {notice && <StatusNotice type="success">{notice}</StatusNotice>}

    <section className="surface-section">
      <div className="request-facts"><span><MapPin/> {request.preferred_area || request.campus_name}</span><span><ShieldCheck/> Maximum {request.max_responses} agency responses</span></div>
      <div className="chip-row">{(request.requiredAmenities || []).map((item: string) => <span className="chip" key={item}>{item}</span>)}</div>
      {request.notes && <p>{request.notes}</p>}
    </section>

    <section className="section-block">
      <div className="section-heading"><div><span className="eyebrow">Matched agency responses</span><h2>Compare structured hostel offers</h2></div></div>
      {data.offers.length ? <div className="request-offer-grid">{data.offers.map(offer => <article className="request-offer-card" key={offer.id}>
        <img src={offer.imageUrl || '/hostels/photos/royal-gate-exterior.webp'} alt="" loading="lazy" decoding="async"/>
        <div>
          <span className="status-pill">Confirmed {offer.availability_confirmed_at ? new Date(offer.availability_confirmed_at).toLocaleDateString() : 'recently'}</span>
          <h2>{offer.hostel_name}</h2>
          <p><MapPin size={14}/> {formatDistance(offer.distance_meters)}</p>
          <div className="agency-meta"><Star size={14} fill="currentColor"/> {Number(offer.average_rating || 0).toFixed(1)} · {offer.verified_placement_count || 0} verified placements</div>
          <p>{offer.message}</p>
          <div className="offer-strip"><span><small>Current hostel price</small><strong>{formatMoney(offer.price_amount)}</strong></span><span><small>Availability confirmed</small><strong>{offer.availability_confirmed_at ? new Date(offer.availability_confirmed_at).toLocaleDateString() : 'Recently'}</strong></span></div>
          <div className="card-actions">
            <Link className="secondary-button" to={`/hostels/${offer.hostel_slug}`}>Open hostel</Link>
            {requestOpen && <button className="ghost-button" disabled={busyOfferId === offer.id || offer.status === 'shortlisted'} onClick={() => void shortlist(offer.id)}><CheckCircle2 size={16}/> {offer.status === 'shortlisted' ? 'Shortlisted' : 'Shortlist'}</button>}
            {requestOpen && <button className="secondary-button" disabled={busyOfferId === offer.id} onClick={() => void message(offer)}><MessageSquareText size={16}/> Ask first</button>}
            {requestOpen && <button className="primary-button" disabled={busyOfferId === offer.id} onClick={() => void accept(offer.id)}>{busyOfferId === offer.id ? 'Opening…' : 'Choose this offer'}</button>}
          </div>
        </div>
      </article>)}</div> : <EmptyState title="No offers yet" description="Matched agencies must attach real hostel cards. You will receive an email when an unread response arrives."/>}
    </section>
  </div>;
}
